/**
 * Sanitize i18n message object keys by replacing dots with underscores.
 * next-intl treats dots as namespace separators, which causes INVALID_KEY errors
 * when backend API returns keys containing dots (e.g., "Xanh b.đêm logo mới").
 *
 * Recursively processes nested objects while preserving their structure.
 */
export function sanitizeI18nKeys(
  obj: Record<string, unknown>
): Record<string, unknown> {
  return Object.keys(obj).reduce<Record<string, unknown>>((acc, key) => {
    const sanitizedKey = key.replace(/\./g, '_')
    const value = obj[key]
    acc[sanitizedKey] =
      typeof value === 'object' && value !== null && !Array.isArray(value)
        ? sanitizeI18nKeys(value as Record<string, unknown>)
        : value
    return acc
  }, {})
}

/**
 * Sanitize a single i18n key for lookup.
 * Must match the transformation applied by `sanitizeI18nKeys`.
 */
export function sanitizeI18nKey(key: string): string {
  return key.replace(/\./g, '_')
}

/**
 * Safely translate a dynamic color key.
 * Uses t.has() to check if the key exists BEFORE calling t(),
 * avoiding MISSING_MESSAGE errors entirely — no throw, no internal resolution overhead.
 *
 * If the key doesn't exist in messages, returns the raw color string.
 */
export function safeTranslateColor(
  t: ((key: string) => string) & { has: (key: string) => boolean },
  colorKey: string
): string {
  if (!colorKey) return ''
  const sanitized = sanitizeI18nKey(colorKey)
  return t.has(sanitized) ? t(sanitized) : colorKey
}
